import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Plus, History, Pencil, Ban, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, DateInput, Select, Field, Input, inputCls, Spinner, StatusBadge } from "../../components/ui-kit";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";

const SEV = { bassa: "blu", media: "giallo", alta: "rosso" };
const ST = { open: { s: "rosso", l: "Aperta" }, verified: { s: "giallo", l: "In verifica" }, closed: { s: "verde", l: "Chiusa" }, cancelled: { s: "grigio", l: "Annullata" } };
const EFFECTIVENESS_DEFAULT = "AZIONE CORRETTIVA ESEGUITA E VERIFICATA: PROBLEMA RISOLTO";
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const italianDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("-") : "");
const isOverdue = (nc) => ["open", "verified"].includes(nc.status) && nc.deadline && nc.deadline.slice(0, 10) <= todayIso();

export default function NonConformita() {
  const { ctx } = useOutletContext();
  const [list, setList] = useState(null);
  const [form, setForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [closing, setClosing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const canManage = ["titolare", "responsabile"].includes(ctx.user.role);
  const canCreate = ["titolare", "responsabile", "addetto"].includes(ctx.user.role);

  const load = () => api.get("/app/nonconformities").then(({ data }) => setList(data.nonconformities)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (nc, status) => {
    try { await api.patch(`/app/nonconformities/${nc.id}`, { status }); toast.success("Aggiornata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="nc-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Gestione</p>
          <div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Non conformità</h1><AcademyTitleBadge /></div>
        </div>
        {canCreate && <Button variant="primary" onClick={() => setForm(true)} data-testid="nc-new"><Plus className="w-4 h-4" /> Segnala non conformità</Button>}
      </div>

      {!list ? <Spinner /> : list.length === 0 ? (
        <Card className="p-10 text-center text-slate-400"><AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-40" />Nessuna non conformità registrata.</Card>
      ) : (
        <div className="space-y-4">
          {list.map((nc) => (
            <Card key={nc.id} className="p-5" data-testid={`nc-${nc.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={SEV[nc.severity] || "giallo"}>Gravità {nc.severity}</StatusBadge>
                    <StatusBadge status={ST[nc.status]?.s || "rosso"}>{ST[nc.status]?.l}</StatusBadge>
                    {isOverdue(nc) && <StatusBadge status="rosso" testid={`nc-overdue-${nc.id}`}>Scaduta</StatusBadge>}
                    <span className="text-[11px] text-slate-400">{nc.source === "auto" ? "Automatica" : nc.source === "recall" ? "Da ritiro prodotto" : "Manuale"}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-navy mt-2">{nc.cause}</h3>
                  {nc.corrective_action && <p className="text-sm text-slate-500 mt-1 font-body">Azione correttiva: {nc.corrective_action}</p>}
                  {nc.responsible && <p className="text-xs text-slate-400 mt-1">Responsabile: {nc.responsible}</p>}
                  {nc.deadline && <p className={`text-xs mt-1 ${isOverdue(nc) ? "font-semibold text-red-600" : "text-slate-400"}`} data-testid={`nc-deadline-${nc.id}`}>Scadenza azione correttiva: {italianDate(nc.deadline)}{isOverdue(nc) ? " — SCADUTA" : ""}</p>}
                  {nc.effectiveness_note && <p className="text-xs text-emerald-700 mt-1">Verifica di efficacia: {nc.effectiveness_note}</p>}
                  {nc.cancel_reason && <p className="text-xs text-slate-500 mt-1">Annullata: {nc.cancel_reason}</p>}
                </div>
                {canManage && (
                  <div className="flex flex-wrap gap-2">
                    {nc.status !== "cancelled" && <Button variant="accent" onClick={() => setEditing(nc)} data-testid={`nc-edit-${nc.id}`}><Pencil className="h-4 w-4" /> Modifica</Button>}
                    {nc.status === "open" && <Button variant="outline" onClick={() => setStatus(nc, "verified")} data-testid={`nc-verify-${nc.id}`}>In verifica</Button>}
                    {["open", "verified"].includes(nc.status) && <Button variant="success" onClick={() => setClosing(nc)} data-testid={`nc-close-${nc.id}`}>Chiudi</Button>}
                    {nc.status !== "cancelled" && <Button variant="outline" onClick={() => setCancelling(nc)} data-testid={`nc-cancel-${nc.id}`} aria-label={`Annulla non conformità ${nc.cause}`}><Ban className="h-4 w-4" /> Annulla</Button>}
                  </div>
                )}
              </div>
              {nc.history?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-hairline flex items-center gap-2 text-xs text-slate-400">
                  <History className="w-3.5 h-3.5" /> {nc.history.length} eventi · ultimo: {nc.history[nc.history.length - 1].action}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {form && <NCForm ctx={ctx} onClose={() => setForm(false)} onDone={() => { setForm(false); load(); }} />}
      {editing && <NCForm ctx={ctx} initial={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {closing && <NCActionDialog testid="nc-close-dialog" title="Chiudi la non conformità" description="Prima di chiudere conferma che l'azione correttiva ha risolto il problema (verifica di efficacia)."
        label="Esito della verifica di efficacia" initial={EFFECTIVENESS_DEFAULT} minLength={3} confirmLabel="Chiudi non conformità" confirmVariant="success" icon={CheckCircle2}
        onCancel={() => setClosing(null)}
        onConfirm={async (note) => { await api.patch(`/app/nonconformities/${closing.id}`, { status: "closed", verify_note: note }); toast.success("Non conformità chiusa"); setClosing(null); load(); }} />}
      {cancelling && <NCActionDialog testid="nc-cancel-dialog" title="Annullare la non conformità?" description={`“${cancelling.cause}” non verrà eliminata: resterà nello storico e nei report come ANNULLATA, con il motivo che scrivi qui.`}
        label="Motivo dell'annullamento" placeholder="ES. APERTA PER ERRORE, DOPPIONE DI UN'ALTRA" minLength={5} confirmLabel="Sì, annulla" confirmVariant="danger" icon={Ban}
        onCancel={() => setCancelling(null)}
        onConfirm={async (reason) => { await api.post(`/app/nonconformities/${cancelling.id}/cancel`, { reason }); toast.success("Non conformità annullata"); setCancelling(null); load(); }} />}
    </div>
  );
}

function NCForm({ ctx, initial = null, onClose, onDone }) {
  const sites = ctx.sites || [];
  const [siteId, setSiteId] = useState(initial?.site_id || sites[0]?.id || "");
  const [severity, setSeverity] = useState(initial?.severity || "media");
  const [cause, setCause] = useState(initial?.cause || "");
  const [action, setAction] = useState(initial?.corrective_action || "");
  const [responsible, setResponsible] = useState(initial?.responsible || "");
  const [deadline, setDeadline] = useState(initial?.deadline || "");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!cause.trim()) { toast.error("Indica la causa"); return; }
    setLoading(true);
    try {
      if (initial) await api.patch(`/app/nonconformities/${initial.id}`, { severity, cause: cause.toUpperCase(), corrective_action: action.toUpperCase(), responsible: responsible.toUpperCase(), deadline: deadline || null });
      else await api.post("/app/nonconformities", { site_id: siteId, severity, cause: cause.toUpperCase(), corrective_action: action.toUpperCase(), responsible: responsible.toUpperCase(), deadline: deadline || null });
      toast.success(initial ? "Non conformità modificata" : "Non conformità segnalata"); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="nc-modal">
      <div className="absolute inset-0 bg-navy/50" onClick={onClose} />
      <Card className="relative w-full sm:max-w-lg p-6 rounded-b-none sm:rounded-xl">
        <h3 className="font-heading text-xl font-semibold mb-4">{initial ? "Modifica non conformità" : "Segnala non conformità"}</h3>
        <div className="space-y-4">
          <Field label="Sede"><Select value={siteId} disabled={Boolean(initial)} onChange={(e) => setSiteId(e.target.value)} data-testid="nc-site">{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
          <Field label="Gravità"><Select value={severity} onChange={(e) => setSeverity(e.target.value)} data-testid="nc-severity"><option value="bassa">Bassa</option><option value="media">Media</option><option value="alta">Alta</option></Select></Field>
          <Field label="Causa / descrizione"><textarea value={cause} onChange={(e) => setCause(e.target.value.toUpperCase())} rows={2} className={inputCls} data-testid="nc-cause" /></Field>
          <Field label="Azione correttiva (facoltativa)"><Input value={action} onChange={(e) => setAction(e.target.value.toUpperCase())} data-testid="nc-action" /></Field>
          <Field label="Responsabile (facoltativo)"><Input value={responsible} onChange={(e) => setResponsible(e.target.value.toUpperCase())} data-testid="nc-responsible" /></Field>
          <Field label="Scadenza azione correttiva (facoltativa)"><DateInput valueMode="iso" value={deadline} onChange={(e) => setDeadline(e.target.value)} aria-label="Scadenza dell'azione correttiva" data-testid="nc-deadline" /></Field>
        </div>
        <div className="flex gap-3 mt-5">
          <Button variant="ghost" className="flex-1" onClick={onClose}>Annulla</Button>
          <Button variant="success" className="flex-1" loading={loading} onClick={submit} data-testid="nc-submit">{initial ? "Salva modifiche" : "Segnala"}</Button>
        </div>
      </Card>
    </div>
  );
}

function NCActionDialog({ title, description, label, initial = "", placeholder, minLength, confirmLabel, confirmVariant, icon: Icon, testid, onCancel, onConfirm }) {
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const ok = text.trim().length >= minLength;
  const submit = async () => {
    setBusy(true);
    try { await onConfirm(text.trim().toUpperCase()); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center p-0 sm:p-6" data-testid={testid}>
      <div className="absolute inset-0 bg-navy/50" onClick={() => !busy && onCancel()} />
      <Card className="relative w-full sm:max-w-lg p-6 rounded-b-none sm:rounded-xl">
        <h3 className="font-heading text-xl font-semibold">{title}</h3>
        <p className="text-sm text-slate-500 mt-2 font-body">{description}</p>
        <div className="mt-4">
          <Field label={label}><textarea value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value.toUpperCase())} rows={3} className={inputCls} data-testid={`${testid}-text`} /></Field>
        </div>
        <div className="flex gap-3 mt-5">
          <Button variant="ghost" className="flex-1" disabled={busy} onClick={onCancel}>Indietro</Button>
          <Button variant={confirmVariant} className="flex-1" loading={busy} disabled={!ok} onClick={submit} data-testid={`${testid}-confirm`}><Icon className="h-4 w-4" /> {confirmLabel}</Button>
        </div>
      </Card>
    </div>
  );
}
